import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import StudentList from './StudentList';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { formatDateReadable } from '../utils/dateUtils';
import { normalizeSession, getBatchesArray } from '../utils/sessionUtils';
import { 
  MdArrowBack, 
  MdSchedule, 
  MdPeople, 
  MdSubject,
  MdCheckCircle,
  MdCancel,
  MdError
} from 'react-icons/md';

/**
 * View Attendance Component
 * Read-only view of marked attendance for a session
 */
function ViewAttendance() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAttendanceAuth();
  const returnToHistory = new URLSearchParams(location.search).get('return') === 'history';
  const returnPath = returnToHistory
    ? '/attendance/tutor/history'
    : '/attendance/tutor/today';
  const returnLabel = returnToHistory
    ? 'Back to History'
    : "Back to Today's Sessions";
  
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Validate session ID first
    if (!sessionId || sessionId === 'undefined') {
      setError('Invalid session ID');
      setLoading(false);
      return;
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      setError('Invalid session ID format');
      setLoading(false);
      return;
    }
    
    fetchAttendanceData();
  }, [sessionId]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate session ID before API call
      if (!sessionId || sessionId === 'undefined') {
        setError('Session ID is required');
        return;
      }
      
      const sessionIdNum = parseInt(sessionId);
      if (isNaN(sessionIdNum) || sessionIdNum <= 0) {
        setError('Invalid session ID format');
        return;
      }
      
      const token = getToken();
      const result = await attendanceAPI.viewAttendance(sessionIdNum, token);
      
      if (result.success) {
        setSession(normalizeSession(result.session));
        setStudents(result.students || []);
        setStats(result.summary || result.statistics || null);
        
        // Map attendance data
        const attendanceMap = {};
        (result.students || []).forEach(student => {
          const studentId = student.student_id || student.id;
          attendanceMap[studentId] = student.is_present ? 'present' : 'absent';
        });
        setAttendance(attendanceMap);
      } else {
        setError(result.message || 'Failed to load attendance data');
      }
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError(err.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <MdError className="text-red-500" />
          <h3 className="text-red-800 font-semibold">Error</h3>
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate(returnPath)}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
        >
          {returnLabel}
        </button>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const presentCount = stats?.present || Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = stats?.absent || Object.values(attendance).filter(s => s === 'absent').length;
  const totalCount = stats?.total || students.length;
  const attendanceRate = totalCount > 0 ? ((presentCount / totalCount) * 100).toFixed(1) : 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(returnPath)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <MdArrowBack />
          <span>{returnLabel}</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-800 mb-4">View Attendance</h1>

        {/* Session Info Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <MdSubject className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-semibold text-gray-800">{session.subject || 'N/A'}</p>
              </div>
            </div>

            {session.batches && (
              <div className="flex items-center space-x-3">
                <MdPeople className="text-blue-500 text-xl" />
                <div>
                  <p className="text-sm text-gray-500">Batch(es)</p>
                  <p className="font-semibold text-gray-800">
                    {getBatchesArray(session.batches).join(', ')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <MdSchedule className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Date & Time</p>
                <p className="font-semibold text-gray-800">
                  {session.session_date ? formatDateReadable(session.session_date) : 'N/A'}
                  {session.time_start && session.time_end && (
                    <span className="block text-sm font-normal text-gray-600">
                      {session.time_start} - {session.time_end}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {session.period && (
              <div className="flex items-center space-x-3">
                <MdSchedule className="text-blue-500 text-xl" />
                <div>
                  <p className="text-sm text-gray-500">Period</p>
                  <p className="font-semibold text-gray-800">{session.period}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Card */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Attendance Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800">{totalCount}</p>
              <p className="text-sm text-gray-600">Total Students</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1">
                <MdCheckCircle className="text-green-500" />
                <p className="text-2xl font-bold text-green-600">{presentCount}</p>
              </div>
              <p className="text-sm text-gray-600">Present</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1">
                <MdCancel className="text-red-500" />
                <p className="text-2xl font-bold text-red-600">{absentCount}</p>
              </div>
              <p className="text-sm text-gray-600">Absent</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{attendanceRate}%</p>
              <p className="text-sm text-gray-600">Attendance Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Student List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <StudentList
          students={students}
          attendance={attendance}
          onAttendanceChange={() => {}} // No-op for read-only
          readOnly={true}
          showSearch={true}
        />
      </div>
    </div>
  );
}

export default ViewAttendance;

